import { SearchIcon } from 'lucide-react';

import { NotificationBell } from '@/features/notifications';
import { WorkspaceLimitChip, useLimitState } from '@/features/spend-limits';
import { useWorkspace } from '@/features/workspaces';
import { Breadcrumbs } from './Breadcrumbs';
import { WorkspaceAccessChip } from './WorkspaceAccessChip';
import { useCommandPaletteStore } from '@/shared/store/commandPalette';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import { modKey } from '@/shared/utils/modKey';

/**
 * What the workspace shell puts in the top bar.
 *
 * The bar itself, and the collapse control at its left, belong to `ShellFrame` — FR-018
 * asks the console for the same behaviour *and the same control*, and two copies of a
 * toggle is how "the same" stops being true. What is left here is what only a workspace
 * has: where you are in it, and the way into the palette.
 *
 * It also carries the workspace's spend state, when there is one to carry (spec 101
 * US3) — the one thing in the bar that is about the workspace rather than about where
 * you are in it. It is absent almost all the time, which is the point.
 *
 * The notifications bell is last (spec 131). It was removed while nothing produced a
 * notification — a disabled control that answered nothing — and returns now that
 * the service keeps an inbox for every person.
 *
 * On a phone the two chips are not here (spec 260): beside the menu, search
 * and the bell they left the page's name its first letter. They open the
 * navigation sheet instead (`PhoneNavStatus`), and the limit leaves a dot on
 * the menu button.
 */
export interface TopbarProps {
  /** Whose spend state the bar shows. The shell already holds it. */
  workspaceId: string;
}

export function Topbar({ workspaceId }: TopbarProps) {
  const openPalette = useCommandPaletteStore((state) => state.setOpen);
  const { data: workspace } = useWorkspace(workspaceId);
  const limits = useLimitState(workspaceId);
  const isPhone = usePhoneWidth();

  return (
    <>
      <Breadcrumbs className="flex-1" />

      {/*
        Before the palette control rather than after it: the state is read, the
        control is used, and a thing to read belongs on the side the eye arrives
        from. Nothing is drawn at all unless the figures say something.
      */}
      {isPhone ? null : (
        <WorkspaceLimitChip
          workspaceId={workspaceId}
          workspaceName={workspace?.name ?? 'This workspace'}
          state={limits.workspace}
        />
      )}

      {/* "View only" or "Organization admin", when this is not a member's view (spec 188). */}
      {isPhone ? null : (
        <WorkspaceAccessChip
          workspaceId={workspaceId}
          workspaceName={workspace?.name ?? 'This workspace'}
        />
      )}

      {/*
        This affordance advertised `⌘K` on a disabled input from the skeleton onward —
        its own comment said it was "wired to nothing… disabled so it cannot be mistaken
        for working", which was honest while nothing answered that key.

        Spec 077 made the key real, and a dead control promising a shortcut that now
        exists is worse than no control: a reader who clicks it learns the product is
        broken, and a reader who never presses ⌘K never finds the palette at all. It is
        a button now, and it opens the thing it names.

        Present at every width. It was `hidden md:flex`, which left a narrow window
        with no way into the palette at all: the shortcut it advertises needs a
        keyboard, and a tablet has none. Below `md` the label and the key hint go
        and the icon stays, still named for assistive technology.
      */}
      <button
        type="button"
        onClick={() => {
          openPalette(true);
        }}
        aria-label="Search and commands"
        className="border-border bg-muted/40 text-muted-foreground hover:bg-accent focus-visible:ring-ring touch-target flex items-center justify-center gap-2 rounded-md border px-2 py-1 text-sm transition-colors focus-visible:ring-3 focus-visible:outline-none"
      >
        <SearchIcon aria-hidden="true" className="size-3.5" strokeWidth={1.5} />
        <span className="hidden w-40 text-left md:inline short-touch:hidden!">
          Search workspace
        </span>
        {/* The platform's modifier — Ctrl on Windows and Linux (spec 129). */}
        <kbd className="border-border hidden rounded border px-1 text-xs md:inline short-touch:hidden!">
          {modKey()}K
        </kbd>
      </button>

      <NotificationBell />
    </>
  );
}
